import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export type RequestConfig = AxiosRequestConfig;
export type HttpClientResponseData<ResponseType> = AxiosResponse<ResponseType>;
export type HttpClientAsyncResponse<ResponseType> = Promise<HttpClientResponseData<ResponseType>>;

type HttpProvider = typeof axios;

export interface ErrorMessage {
  errorMessage: string;
  errorCode: string;
}

export class HttpError extends Error {
  constructor(error: AxiosError<{ messages: ErrorMessage[] }>) {
    let errorMessages: ErrorMessage[];
    if (error.response?.headers['content-type']?.includes('application/json')) {
      errorMessages = error.response?.data.messages ?? [];
    } else {
      errorMessages = [];
    }

    super(error.message);
    this.errorMessages = errorMessages;
  }

  errorMessages: ErrorMessage[];
}

export type GetFunction = <ReturnType>(url: string, config?: RequestConfig) => HttpClientAsyncResponse<ReturnType>;
export type PostFunction = <DataType, ReturnType = DataType>(
  url: string,
  data?: DataType,
  config?: RequestConfig
) => HttpClientAsyncResponse<ReturnType>;
export type PutFunction = <DataType, ReturnType = DataType>(
  url: string,
  data?: DataType,
  config?: RequestConfig
) => HttpClientAsyncResponse<ReturnType>;
export type DeleteFunction = (url: string, id: string, config?: RequestConfig) => HttpClientAsyncResponse<string>;

export type CancelToken = HttpProvider['CancelToken'];

export interface HttpClient {
  get: GetFunction;
  post: PostFunction;
  put: PutFunction;
  delete: DeleteFunction;
  CancelToken: CancelToken;
}

const httpClientFactory = (http: HttpProvider): HttpClient => ({
  get: (url, config) =>
    http.get(url, config).catch((error) => {
      throw new HttpError(error);
    }),
  post: (url, data, config?) =>
    http.post(url, data, config).catch((error) => {
      throw new HttpError(error);
    }),
  put: (url, data, config) =>
    http.put(url, data, config).catch((error) => {
      throw new HttpError(error);
    }),
  delete: (url, id, config) =>
    http.delete(`${url}${id}`, config).catch((error) => {
      throw new HttpError(error);
    }),
  CancelToken: http.CancelToken,
});

axios.defaults.baseURL = process.env.REACT_APP_MOCK_API === 'true' ? '' : process.env.REACT_APP_API_URL;

const httpClient = httpClientFactory(axios);

export { httpClient, httpClientFactory };
